import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { DocumentNode } from 'graphql';
import buildQueryKey from '../../utils/buildQueryKey';
import fetchEdge from '../../utils/fetchEdge';

interface useEdgeQueryOptions<T> extends Dict {
  variables?: T;
}

/**
 *
 * @param query
 * @param options
 */
const useEdgeQuery = <
  TQueryFnData extends Dict = Dict,
  TVariable extends Dict = Dict,
  TData = TQueryFnData,
>(
  query: DocumentNode,
  options: useEdgeQueryOptions<TVariable> = {},
) => {
  const { variables = {}, context = {}, ...queryOptions } = options;

  const queryFn = useCallback(
    (queryKey) => fetchEdge<TQueryFnData>(queryKey, query, { context }),
    [context, query],
  );

  const { status, isInitialLoading, ...queryObject } = useQuery<
    TQueryFnData,
    Error,
    TData
  >(buildQueryKey({ query, variables }), queryFn, {
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    ...queryOptions,
  });

  return {
    loading: isInitialLoading,
    status,
    ...queryObject,
  };
};

export default useEdgeQuery;
